import {css} from '@emotion/css';
import {Constants, CustomerItem, marketToTimezone, renderDate} from '@remoov/functional';
import {Button, theme} from '@remoov/remoov-components';
import React, {ButtonHTMLAttributes, HTMLAttributes, useState} from 'react';
import {
  BoldInfoText,
  HideOnLargeScreen,
  HideOnSmallScreen,
  ImageBox,
  InfoText,
  ItemsBoardColumn,
  ItemsBoardContainer,
  ItemsBoardDivisionLine,
  ItemsBoardLeftContainer,
  ItemsBoardRightContainer,
  ItemsBoardRow,
} from './Common';

const {
  EndOfConsignmentAction: {DONATE, RETURN},
} = Constants;

export type ExpiredBoardItemProps = {
  item: CustomerItem;
  imgUrl: string;
  alt: string;
  id: string | number;
  description: string;
  quantity: number;
  expired: string;
  market: string;
  onClick: () => unknown;
  donateAction: () => unknown;
  returnAction: () => unknown;
  actionSelected?: Maybe<string>;
};

export const ExpiredBoardItem = ({
  imgUrl,
  alt,
  id,
  description,
  quantity,
  expired,
  market,
  onClick,
  donateAction,
  returnAction,
  actionSelected,
  item,
}: ExpiredBoardItemProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const timezone = marketToTimezone(market);
  const actionComponent = (buttonAditionalClassName?: string) =>
    actionSelected === null ? (
      <ActionSelector
        {...{isOpen, setIsOpen, donateAction, returnAction}}
        buttonAditionalClassName={buttonAditionalClassName}
      />
    ) : (
      <SelectedActionText
        endOfConsignmentDate={item.end_of_consignment_pickup_at}
        {...{actionSelected, timezone}}
      />
    );
  return (
    <ItemsBoardContainer
      className={css({backgroundColor: theme.colors.white, paddingTop: theme.spacings.small})}
    >
      <ItemsBoardRow
        className={css({
          [theme.mediaMaxWidth.small_medium]: {
            flexWrap: 'wrap',
            padding: `0 ${theme.spacings.xsmall}`,
          },
        })}
      >
        <ImageBox
          onClick={onClick}
          className={css({
            margin: `0 ${theme.spacings.large}`,
            [theme.mediaMaxWidth.small_medium]: {width: '100%', margin: '0 auto'},
          })}
        >
          <img src={imgUrl} alt={alt} />
        </ImageBox>

        <ItemsBoardLeftContainer
          className={css({
            [theme.mediaMaxWidth.large]: {
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: theme.spacings.xxsmall,
            },
            [theme.mediaMaxWidth.medium]: {width: 'auto'},
          })}
        >
          <ItemsBoardColumn className={css({maxWidth: '100px'})}>
            <BoldInfoText>{id}</BoldInfoText>
          </ItemsBoardColumn>
          <ItemsBoardColumn>
            <InfoText>{description}</InfoText>
          </ItemsBoardColumn>
        </ItemsBoardLeftContainer>

        <HideOnSmallScreen>
          <ItemsBoardRightContainer className={css({paddingLeft: theme.spacings.large})}>
            <ItemsBoardColumn>
              <InfoText>{quantity}</InfoText>
            </ItemsBoardColumn>
            <ItemsBoardColumn>
              <InfoText>{renderDate(expired, timezone)}</InfoText>
            </ItemsBoardColumn>
            <ItemsBoardColumn>{actionComponent(css({maxWidth: '125px'}))}</ItemsBoardColumn>
          </ItemsBoardRightContainer>
        </HideOnSmallScreen>

        <HideOnLargeScreen className={css({padding: theme.spacings.xxsmall})}>
          <ItemsBoardColumn className={css({div: {marginBottom: theme.spacings.xxsmall}})}>
            <ItemsBoardRow>
              <BoldInfoText className={css({marginRight: theme.spacings.xxsmall})}>
                Quantity:{' '}
              </BoldInfoText>
              <InfoText>{quantity}</InfoText>
            </ItemsBoardRow>
            <ItemsBoardRow>
              <BoldInfoText className={css({marginRight: theme.spacings.xxsmall})}>
                Expires on:{' '}
              </BoldInfoText>
              <InfoText>{renderDate(expired, timezone)}</InfoText>
            </ItemsBoardRow>
          </ItemsBoardColumn>
        </HideOnLargeScreen>
      </ItemsBoardRow>

      <HideOnLargeScreen>
        <ItemsBoardRow>{actionComponent()}</ItemsBoardRow>
      </HideOnLargeScreen>
      <ItemsBoardDivisionLine className={css({marginTop: theme.spacings.small})} />
    </ItemsBoardContainer>
  );
};
const ActionSelector = ({
  isOpen,
  setIsOpen,
  donateAction,
  returnAction,
  buttonAditionalClassName,
}: {
  isOpen: boolean;
  setIsOpen: (arg: boolean) => unknown;
  donateAction: () => unknown;
  returnAction: () => unknown;
  buttonAditionalClassName?: string;
}) => (
  <div className={css({position: 'relative'})}>
    <Button
      buttonStyle='primary'
      onClick={() => setIsOpen(!isOpen)}
      className={buttonAditionalClassName}
    >
      Actions
    </Button>
    <ActionOptionsContainer isOpen={isOpen}>
      <Option onClick={donateAction}>Donate</Option>
      <Option onClick={returnAction}>Pickup</Option>
    </ActionOptionsContainer>
  </div>
);
const ActionOptionsContainer = ({
  isOpen,
  ...rest
}: HTMLAttributes<HTMLDivElement> & {isOpen: boolean}) => (
  <ItemsBoardColumn
    className={css({
      width: '188px',
      background: theme.colors.white,
      position: 'absolute',
      zIndex: 99,
      bottom: '-235%',
      right: 0,
      pointerEvents: isOpen ? 'all' : 'none',
      opacity: isOpen ? 1 : 0,
      transition: 'opacity 0.3s ease-in-out',
    })}
    {...rest}
  />
);
const Option = ({children, ...rest}: ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    className={css({
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: `${theme.spacings.xsmall} ${theme.spacings.small}`,
      background: 'transparent',
      border: `1px solid ${theme.colors.lightBlue}`,
      cursor: 'pointer',
      '&hover': {background: theme.colors.lightBlue},
    })}
    {...rest}
  >
    <InfoText>{children}</InfoText>
  </button>
);
const SelectedActionText = ({
  timezone,
  actionSelected,
  endOfConsignmentDate,
}: {
  timezone: string;
  actionSelected?: string;
  endOfConsignmentDate?: Maybe<string>;
}) => (
  <BoldInfoText className={css({color: theme.colors.darkBlue})}>
    {actionSelected === DONATE
      ? 'Item will be donated'
      : actionSelected === RETURN
      ? endOfConsignmentDate
        ? `Item will be picked up at ${renderDate(endOfConsignmentDate, timezone)}`
        : 'Item will be picked up'
      : actionSelected}
  </BoldInfoText>
);
